import { Badge, Box, Flex, Grid, IconButton, Text, useColorModeValue } from '@chakra-ui/react'
import { IconChevronUp as ChevronUp } from '@tabler/icons-react'
import React from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { AnsweredBy, KnowledgeSource } from '../../types/chatTypes'
import { getAgentIcon } from '../AgentIcon'
import WebSearchQueries from './WebSearchQueries'

interface KnowledgeSourceBadgeProps {
  knowledgeSource?: KnowledgeSource // None yet: no block
  author?: AnsweredBy
  webSearchQueries?: string[]
  canMinimize?: boolean
  onMinimize?: () => void
}

const KnowledgeSourceBadge: React.FC<KnowledgeSourceBadgeProps> = ({
  knowledgeSource,
  author,
  webSearchQueries,
  canMinimize,
  onMinimize,
}) => {
  const { t } = useLanguage()

  const hoverBg = useColorModeValue('gray.100', 'gray.600')

  // The block is the one place that names the source; what runs shows in the status line below.
  const badgeLabel =
    knowledgeSource === 'INTERNAL_KB'
      ? t('knowledge_source.badge_internal')
      : knowledgeSource === 'INTERNET_SEARCH'
        ? t('knowledge_source.badge_internet')
        : t('knowledge_source.badge_ai')

  const handleMinimizeClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent event bubbling
    onMinimize?.()
  }

  return (
    <Grid templateColumns='auto 1fr' columnGap={2} alignItems='center' w='100%'>
      <Flex align='center' gap={2}>
        {/* Every source in the client colour: the tint of a dark primary is near-grey,
            so a neutral internet block did not stand apart. The text names the source. */}
        {knowledgeSource && (
          <Badge
            bg='chat.iconSurface'
            borderRadius='chat.sm'
            color='chat.iconInk'
            fontSize='sm'
            px={2}
            py={1}
            textTransform='none'
            fontWeight='semibold' // Added for better contrast
          >
            {badgeLabel}
          </Badge>
        )}
      </Flex>
      {/* The badge keeps its place on every answer; the author follows it. */}
      <Flex align='center' gap={2} minW={0}>
        {author && (
          <Flex align='center' gap={1.5} minW={0} data-testid='answer-author'>
            <Flex color='chat.muted' flexShrink={0}>
              {getAgentIcon(author.profile_picture_url, 14, true)}
            </Flex>
            <Text fontSize='sm' fontWeight='semibold' color='chat.ink' noOfLines={1}>
              {author.name}
            </Text>
          </Flex>
        )}
        {canMinimize && onMinimize && (
          <IconButton
            aria-label={t('common.minimize')}
            icon={<ChevronUp size={16} />}
            size='xs'
            variant='ghost'
            colorScheme='gray'
            onClick={handleMinimizeClick}
            _hover={{ bg: hoverBg }}
          />
        )}
      </Flex>
      {!!webSearchQueries?.length && (
        // Pulled up against the author: the block makes the first row taller than the name.
        <Box gridColumn={2} mt={-1.5} minW={0}>
          <WebSearchQueries queries={webSearchQueries} />
        </Box>
      )}
    </Grid>
  )
}

export default KnowledgeSourceBadge
